import { useEffect } from "react";

import { EntityId } from "@reduxjs/toolkit";

import { Alert, CircularProgress } from "@mui/material";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { playbackActions } from "./store/slice";
import { playbackSessionSelectors } from "./store/selectors.session";

import {
    useCreatePlaybackSessionMutation,
    useExtendPlaybackSessionQuery,
} from "services/streamingApi/streamingPodManager.api";

import { PlaybackConfiguration } from "types/AppConfig";

import { PlaybackControls } from "./PlaybackControls";
import { PlaybackDeviceGrid } from "./PlaybackDeviceGrid";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { FeatureFlagGuard } from "features/appFeatures/FeatureFlagGuard";
import { PlaybackDebugControls } from "./PlaybackDebugControls";

export interface PlaybackProps {
    siteId: EntityId;
    startDateTime: string;
    endDateTime: string;
    playbackConfiguration: PlaybackConfiguration;
    drawerWidth: number;
}

export function PlaybackSession(props: PlaybackProps) {
    const dispatch = useAppDispatch();

    const sessionId = useAppSelector(playbackSessionSelectors.selectSessionId);

    // create session per selected site
    const [createPlaybackSession, { isLoading, isError, isSuccess }] =
        useCreatePlaybackSessionMutation();

    useEffect(() => {
        if (sessionId) return;

        let abortRequestCallback: () => void;

        const createSessionId = async () => {
            const request = createPlaybackSession({ siteId: props.siteId });
            abortRequestCallback = request.abort;

            const sessionId = await request.unwrap();
            dispatch(playbackActions.setSessionId({ sessionId }));
        };
        createSessionId();

        return () => abortRequestCallback && abortRequestCallback();
    }, [sessionId, createPlaybackSession, dispatch, props.siteId]);

    // reset session id when unmounting this component
    useEffect(() => {
        return () => {
            dispatch(playbackActions.resetSessionId());
        };
    }, [dispatch]);

    // keep the session alive while this component is on screen
    useExtendPlaybackSessionQuery(
        {
            sessionId: sessionId ?? "",
            extendBy: props.playbackConfiguration.extendSessionBy,
        },
        {
            skip: !sessionId,
            pollingInterval:
                props.playbackConfiguration.extendSessionPollingIntervalInMs,
            // prevent rerendering by returning an empty object
            selectFromResult: () => ({}),
        },
    );

    return (
        <>
            {isLoading && <CircularProgress />}
            {isError && (
                <Alert severity="error">Error creating playback session</Alert>
            )}
            {isSuccess && sessionId && (
                <>
                    <FeatureFlagGuard
                        accessibleWithFeatureFlags={[
                            AppFeature.AdminDebugTools,
                        ]}
                    >
                        <PlaybackDebugControls />
                    </FeatureFlagGuard>

                    <PlaybackDeviceGrid
                        sessionId={sessionId}
                        siteId={props.siteId}
                        drawerWidth={props.drawerWidth}
                    />
                    <PlaybackControls
                        startDateTime={props.startDateTime}
                        endDateTime={props.endDateTime}
                        playbackConfiguration={props.playbackConfiguration}
                        drawerWidth={props.drawerWidth}
                        displayAsFloatingFooter={true}
                    />
                </>
            )}
        </>
    );
}
